import React, { useState, useEffect } from "react"
import { makeStyles } from 'tss-react/mui';
import {
    Modal,
    Fade,
    Backdrop,
    TextField,
    Tabs,
    Tab,
    Button,
    Typography,
    Tooltip,
} from "@mui/material"
import { Grid, Paper } from "@mui/material"
import { setError } from "../store/features/error/errorSlice"
import { connect } from "react-redux"
import IconButton from "@mui/material/IconButton"
import CloseIcon from "@mui/icons-material/Close"
import ContactAvatar from "./ContactAvatar"
import {
    updatePersonalContact,
    createPersonalContact,
    getPersonalPhonebook,
} from "../utils/utils"
import { areObjectsEqual } from "../utils/common"
import { formatUsPhone } from "../utils/contacts"
import { setPersonalPhonebook } from "../store/features/phonebook/personalPhonebookSlice"

const mapDispatchToProps = {
    setError,
    setPersonalPhonebook,
};

const useStyles = makeStyles()((theme) => ({
    modal: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: theme.spacing(2),
    },
    paper: {
        // Positioned so it stacks above the modal's backdrop; without this the
        // backdrop covers the card and swallows every click.
        position: "relative",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: 640,
        maxHeight: "calc(100vh - 32px)",
        borderRadius: 12,
        boxShadow: theme.shadows[8],
        outline: "none",
        overflow: "hidden",
    },
    header: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
        padding: theme.spacing(2.5, 1.5, 1.5, 3),
    },
    heading: {
        flex: 1,
        minWidth: 0,
    },
    title: {
        fontSize: 20,
        fontWeight: 500,
        lineHeight: 1.3,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    subtitle: {
        fontSize: 13,
        lineHeight: 1.4,
        color: theme.palette.text.secondary,
    },
    close: {
        alignSelf: "flex-start",
        color: theme.palette.text.secondary,
    },
    // Marks a tab that holds a field to correct.
    tabDot: {
        display: "inline-block",
        width: 7,
        height: 7,
        marginLeft: 6,
        borderRadius: "50%",
        verticalAlign: "middle",
        backgroundColor: "#d0312d",
    },
    tabs: {
        padding: theme.spacing(0, 2),
        minHeight: 42,
        borderBottom: `1px solid ${theme.palette.divider}`,
        "& .MuiTab-root": {
            minHeight: 42,
            textTransform: "none",
            fontSize: 14,
            fontWeight: 500,
        },
    },
    body: {
        flex: 1,
        overflowY: "auto",
        padding: theme.spacing(2.5, 3, 1),
        // An empty field shows a hint, not something that looks like a value:
        // lighter and a little smaller, with the label always above the field.
        "& .MuiInputBase-input::placeholder": {
            fontSize: "0.85em",
            color: theme.palette.text.disabled,
            opacity: 1,
        },
    },
    sectionLabel: {
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: "0.06em",
        lineHeight: 1.4,
        textTransform: "uppercase",
        color: theme.palette.text.secondary,
        margin: theme.spacing(1, 0, 0),
    },
    footer: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        padding: theme.spacing(1.5, 3, 2),
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    spacer: {
        flex: 1,
    },
    textButton: {
        textTransform: "none",
        color: theme.palette.text.secondary,
    },
    primary: {
        textTransform: "none",
        minWidth: 112,
        color: "#fff",
        backgroundColor: theme.palette.primary.light,
        boxShadow: "none",
        "&:hover": {
            backgroundColor: theme.palette.primary.light,
            filter: "brightness(0.93)",
            boxShadow: "none",
        },
    },
}));

function a11yProps(index) {
    return {
        id: `contact-tab-${index}`,
        "aria-controls": `contact-tabpanel-${index}`,
    };
}

// The business number is what gets dialled. The server accepts 3 to 16
// digits and rejects anything else with a 400.
const digitsOnly = new RegExp(/^[0-9]{3,16}$/);

const emailLike = new RegExp(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);

// A number as people type it, "(561) 877-5080", without the punctuation. The
// business number is stored as digits only, so it also loses a leading "+".
// Anything that is not a number is left as typed.
const tidyNumber = (value, digitsOnlyResult) => {
    const text = String(value || "").trim();
    const compact = text.replace(/[\s().-]/g, "");
    if (!/^\+?\d+$/.test(compact)) return text;
    return digitsOnlyResult ? compact.replace(/^\+/, "") : compact;
};

const emptyContact = () => ({
    "firstName": "",
    "lastName": "",
    "trunkAccess": "",
    "email": "",
    "company": "",
    "address": {
        "streetOne": "",
        "streetTwo": "",
        "city": "",
        "state": "",
        "zip": "",
        "county": "",
        "country": ""
    },
    "numbers": {
        "office": "",
        "mobile": "",
        "fax": "",
        "sms": ""
    },
    "info": "",
    "type": "personal",
});

const ContactInfoPersonal = (props) => {
    const { classes } = useStyles();
    const {
        open,
        setOpen,
        edit,
        setEdit,
        create,
        setCreate,
        contact,
        setContact,
        session,
        setPersonalPhonebook,
        setError,
    } = props;

    const [tabValue, setTabValue] = useState(0);
    const [currentContact, setCurrentContact] = useState(contact)
    const [initContact, setInitContact] = useState(contact)
    const [submitting, setSubmitting] = useState(false)
    // Fields the user has left, and whether Save was pressed: errors are only
    // shown then, so a new form does not open covered in red.
    const [touched, setTouched] = useState({})
    const [submitAttempted, setSubmitAttempted] = useState(false)

    const clearState = () => {
        setOpen(false)
        setEdit(false)
        setCreate(false)
        setContact(null)
        setCurrentContact(null)
        setInitContact(null)
    }

    if (create && !currentContact) {
        const blank = emptyContact();
        setCurrentContact(blank)
        setInitContact(blank)
    }

    useEffect(() => {
        setCurrentContact(contact)
        setInitContact(contact)
    }, [contact]);

    // Every time the dialog opens it starts on the first tab without errors.
    useEffect(() => {
        if (open) {
            setTabValue(0)
            setTouched({})
            setSubmitAttempted(false)
            setSubmitting(false)
        }
    }, [open]);

    const handlePropertyChange = (field, value) => {
        setCurrentContact({
            ...currentContact,
            [field]: value,
        });
    };

    const handleSubPropertyChange = (category, field, value) => {
        setCurrentContact({
            ...currentContact,
            [category]: {
                ...currentContact[category],
                [field]: value,
            },
        });
    };

    const markTouched = (key) => setTouched((prev) => ({ ...prev, [key]: true }));

    const saveContact = async () => {
        setSubmitting(true);
        try {
            let data = {
                contactDetails: {
                    ...currentContact,
                    email: String(currentContact.email || "").trim(),
                    numbers: {
                        ...(currentContact.numbers || {}),
                        office: tidyNumber((currentContact.numbers || {}).office, true),
                        mobile: tidyNumber((currentContact.numbers || {}).mobile, false),
                        sms: tidyNumber((currentContact.numbers || {}).sms, false),
                        fax: tidyNumber((currentContact.numbers || {}).fax, false),
                    },
                },
                token: session.token,
                server: session.lastServer,
            }
            const result = edit
                ? await updatePersonalContact(data)
                : await createPersonalContact(data);
            if (result instanceof Error) {
                throw result;
            }

            try {
                await getPersonalPhonebook(
                    {
                        token: session.token,
                        server: session.lastServer,
                    },
                    setPersonalPhonebook
                );
                setSubmitting(false);
                setOpen(false)
                setError({
                    visible: true,
                    type: "success",
                    message: edit ? "Contact updated." : "Contact created.",
                });
            } catch (error) {
                setSubmitting(false);
                setError({
                    visible: true,
                    type: "error",
                    message: "The contact was saved, but the list could not be refreshed. " + error,
                })
                setOpen(false)
            }
        } catch (error) {
            setSubmitting(false);
            setError({
                visible: true,
                type: "error",
                message: "Failed to " + (edit ? "update" : "create") + " the contact. " + error,
            })
        }
    };

    const handleReset = () => {
        setCurrentContact(initContact);
        setTouched({});
        setSubmitAttempted(false);
    };

    if (!open || !currentContact || currentContact.type !== "personal")
        return null;

    const readOnly = !edit && !create;

    const office = String((currentContact.numbers || {}).office || "");
    const emailText = String(currentContact.email || "").trim();
    const errors = {
        email: emailText !== "" && !emailLike.test(emailText) ? "Not a valid email address" : "",
        firstName: String(currentContact.firstName || "").trim() === "" ? "Required" : "",
        lastName: String(currentContact.lastName || "").trim() === "" ? "Required" : "",
        office: office === "" ? "Required"
            : !digitsOnly.test(tidyNumber(office, true)) ? "A number of 3 to 16 digits" : "",
    };
    const hasErrors = Object.values(errors).some(Boolean);
    const showError = (key) => !readOnly && (submitAttempted || touched[key]) && errors[key] !== "";
    const isModified = !areObjectsEqual(currentContact, initContact);

    const handleSubmit = () => {
        if (hasErrors) {
            // Show what is missing instead of silently doing nothing.
            setSubmitAttempted(true);
            setTabValue(0);
            return;
        }
        saveContact();
    };

    const fullName = `${currentContact.firstName || ""} ${currentContact.lastName || ""}`.trim();
    const title = create ? "New contact" : edit ? "Edit contact" : (fullName || "Contact");

    // One text field. `area` is "numbers" / "address" for nested values.
    const field = (label, key, area, options = {}) => {
        const errorKey = options.errorKey;
        // A contact from the server may lack a whole block, such as the address.
        const value = area ? (currentContact[area] || {})[key] : currentContact[key];
        return (
            <TextField
                label={label}
                variant="outlined"
                size="small"
                fullWidth
                required={!readOnly && !!errorKey && errorKey !== "email"}
                multiline={!!options.rows}
                rows={options.rows}
                type={options.type}
                value={value || ""}
                onChange={(e) => area
                    ? handleSubPropertyChange(area, key, e.target.value)
                    : handlePropertyChange(key, e.target.value)}
                onBlur={() => {
                    // A phone number loses its punctuation when the field is left.
                    if (options.phone && !readOnly) {
                        const tidy = tidyNumber(value, options.phone === "digits");
                        if (tidy !== String(value || "")) handleSubPropertyChange(area, key, tidy);
                    }
                    if (errorKey) markTouched(errorKey);
                }}
                error={errorKey ? showError(errorKey) : false}
                helperText={errorKey && showError(errorKey)
                    ? errors[errorKey]
                    // How a US number will read in the lists.
                    : options.phone && value && String(formatUsPhone(value)) !== String(value)
                        ? `Shown as ${formatUsPhone(value)}`
                        : options.hint}
                placeholder={readOnly ? "" : (options.placeholder || `Add ${label.toLowerCase()}`)}
                slotProps={{
                    input: { readOnly: readOnly },
                    // Keep the label above the field so the hint can sit inside it.
                    inputLabel: { shrink: true },
                    htmlInput: { inputMode: options.inputMode, autoComplete: "off" },
                }}
            />
        );
    };

    const half = { xs: 12, sm: 6 };

    return (
        <Modal
            aria-labelledby="contact-dialog-title"
            className={classes.modal}
            open={open}
            onClose={clearState}
            closeAfterTransition
            slots={{
                backdrop: Backdrop
            }}
            slotProps={{
                backdrop: {
                    timeout: 300,
                }
            }}
        >
            <Fade in={open}>
                <Paper className={classes.paper}>
                    <div className={classes.header}>
                        <ContactAvatar name={fullName} number={office} size={44} />
                        <div className={classes.heading}>
                            <div id="contact-dialog-title" className={classes.title}>{title}</div>
                            <div className={classes.subtitle}>
                                {create || !fullName || readOnly ? "Personal contact" : fullName}
                            </div>
                        </div>
                        <Tooltip title="Close">
                            <IconButton
                                aria-label="close"
                                className={classes.close}
                                size="small"
                                onClick={clearState}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </div>

                    <Tabs
                        value={tabValue}
                        onChange={(event, newValue) => setTabValue(newValue)}
                        className={classes.tabs}
                    >
                        <Tab
                            label={<span>Details{!readOnly && submitAttempted && hasErrors && <span className={classes.tabDot} title="Something here needs correcting" />}</span>}
                            {...a11yProps(0)}
                        />
                        <Tab label="Address" {...a11yProps(1)} />
                    </Tabs>

                    <div className={classes.body}>
                        <div role="tabpanel" hidden={tabValue !== 0} id="contact-tabpanel-0" aria-labelledby="contact-tab-0">
                            {tabValue === 0 && (
                                <Grid container spacing={2}>
                                    <Grid size={half}>{field("First name", "firstName", null, { errorKey: "firstName" })}</Grid>
                                    <Grid size={half}>{field("Last name", "lastName", null, { errorKey: "lastName" })}</Grid>
                                    <Grid size={half}>{field("Company", "company")}</Grid>
                                    <Grid size={half}>{field("Email", "email", null, { type: "email", placeholder: "name@example.com", errorKey: "email" })}</Grid>

                                    <Grid size={12}>
                                        <Typography component="div" className={classes.sectionLabel}>Phone numbers</Typography>
                                    </Grid>
                                    <Grid size={half}>{field("Business", "office", "numbers", { errorKey: "office", inputMode: "tel", phone: "digits", placeholder: "For example, 5618775080" })}</Grid>
                                    <Grid size={half}>{field("Mobile", "mobile", "numbers", { inputMode: "tel", phone: "any", placeholder: "Add mobile number" })}</Grid>
                                    <Grid size={half}>{field("SMS", "sms", "numbers", { inputMode: "tel", phone: "any", placeholder: "Add SMS number" })}</Grid>
                                    <Grid size={half}>{field("Fax", "fax", "numbers", { inputMode: "tel", phone: "any", placeholder: "Add fax number" })}</Grid>

                                    <Grid size={12}>{field("Notes", "info", null, { rows: 3, placeholder: "Add a note about this contact" })}</Grid>
                                </Grid>
                            )}
                        </div>

                        <div role="tabpanel" hidden={tabValue !== 1} id="contact-tabpanel-1" aria-labelledby="contact-tab-1">
                            {tabValue === 1 && (
                                <Grid container spacing={2}>
                                    <Grid size={12}>{field("Street", "streetOne", "address")}</Grid>
                                    <Grid size={12}>{field("Street, line 2", "streetTwo", "address", { placeholder: "Apartment, suite, floor" })}</Grid>
                                    <Grid size={half}>{field("City", "city", "address")}</Grid>
                                    <Grid size={half}>{field("State", "state", "address")}</Grid>
                                    <Grid size={half}>{field("Zip", "zip", "address", { placeholder: "Add zip code" })}</Grid>
                                    <Grid size={half}>{field("County", "county", "address")}</Grid>
                                    <Grid size={half}>{field("Country", "country", "address")}</Grid>
                                </Grid>
                            )}
                        </div>
                    </div>

                    {readOnly ? null :
                        <div className={classes.footer}>
                            {edit && (
                                <Button
                                    className={classes.textButton}
                                    onClick={handleReset}
                                    disabled={submitting || !isModified}
                                >
                                    Reset
                                </Button>
                            )}
                            <div className={classes.spacer} />
                            <Button className={classes.textButton} onClick={clearState} disabled={submitting}>
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                className={classes.primary}
                                disabled={submitting || !isModified}
                                onClick={handleSubmit}
                            >
                                {submitting ? "Saving…" : edit ? "Save changes" : "Add contact"}
                            </Button>
                        </div>}
                </Paper>
            </Fade>
        </Modal>
    );
}

const mapStateToProps = (state) => {
    return {
        user: state.user,
        session: state.session,
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ContactInfoPersonal);